import { NodeResizer, type NodeProps, useReactFlow } from '@xyflow/react';
import { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { RfNodeTextNode, SweepCanvasRfNode } from '../../canvas-types/nodeTypesData';
import { RichTextEditorProvider } from '../../../common/rich-text-editor/RichTextEditorProvider';
import { RichTextEditor } from '../../../common/rich-text-editor/RichTextEditor';
import { RichTextToolbar } from '../../../common/rich-text-editor/RichTextToolbar';
import { CanvasNodeToolbar } from '../components/CanvasNodeToolbar';
import { NodeActionsMenu } from '../components/NodeActionsMenu';
import { useNodeRichTextEditBehavior } from '../hooks/useNodeRichTextEditBehavior';
import { useNodeDeleteOnKeyPress } from '../hooks/useNodeDeleteOnKeyPress';
import { useNodeResizing } from '../hooks/useNodeResizing';
import { Separator } from '../../../common/rich-text-editor/toolbar-items/Separator';
import { useRichTextEditor } from '../../../common/rich-text-editor/RichTextEditorProvider';
import { ToolbarItemType } from '../../../common/rich-text-editor/toolbar-items/toolbarItemRegistry';
import { CSS_Z_INDEX_NODE_HANDLE } from '../../const/zIndex';

const PLACEHOLDER_TEXT = 'Type anything';

const styles = {
  leftToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '40px',
    borderRadius: '4px',
    backgroundColor: 'white',
  },
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'top',
    justifyContent: 'top',
    borderRadius: '4px',
    '&.drop-target': {
      borderColor: colors.blue[500],
    },
    cursor: 'crosshair',
  },
  handle: {
    width: 12,
    height: 12,
    zIndex: CSS_Z_INDEX_NODE_HANDLE,
  },
  lineStyle: {
    borderColor: colors.blue[500],
    borderWidth: 1,
  },
} as const;

const TEXT_NOTE_MIN_WIDTH = 10;
const TEXT_NOTE_MIN_HEIGHT = 10;

const TEXT_NODE_TOOLBAR_ITEMS: ToolbarItemType[] = [
  'TextColor',
  'Separator',
  'HeadingSelector',
  'Separator',
  'Bold',
  'BulletList',
  'Link',
];

const TextNode = ({
  id,
  selected,
  dragging,
  data: { richText, freshlyCreated },
  height,
}: NodeProps<RfNodeTextNode>) => {
  const { setNodes, updateNode } = useReactFlow<SweepCanvasRfNode>();

  const {
    handleRichTextChange,
    lastEditWasEmpty,
    editMode,
    exclusivelySelected,
    setEditMode,
    multipleNodesSelected,
    saveRichText,
  } = useNodeRichTextEditBehavior({
    id,
    freshlyCreated,
    selected,
    dragging,
    trackEmptyEdits: true,
    currentRichText: richText,
  });

  useNodeDeleteOnKeyPress({
    nodeId: id,
    exclusivelySelected,
    editMode,
  });

  useEffect(() => {
    if (lastEditWasEmpty && !editMode) {
      setNodes((nodes) => nodes.filter((node) => node.id !== id));
    }
  }, [editMode, id, lastEditWasEmpty, setNodes]);

  const { urlEditorOpen } = useRichTextEditor();

  const { resizing, setResizeTrue, setResizeFalse } = useNodeResizing();

  const [textEditorHeight, setTextEditorHeight] = useState<number>(height ?? TEXT_NOTE_MIN_HEIGHT);

  const setNodeHeight = useCallback(
    (height: number) => {
      updateNode(id, { height });
    },
    [id, updateNode],
  );

  const onBlur = useCallback(() => {
    if (textEditorHeight > (height ?? TEXT_NOTE_MIN_HEIGHT) && textEditorHeight !== height) {
      setNodeHeight(textEditorHeight);
    }
    saveRichText();
  }, [setNodeHeight, textEditorHeight, saveRichText, height]);

  return (
    <>
      <NodeResizer
        isVisible={selected && !dragging && !multipleNodesSelected && !editMode}
        handleClassName="section-node-handle"
        handleStyle={styles.handle}
        lineStyle={styles.lineStyle}
        minWidth={TEXT_NOTE_MIN_WIDTH}
        minHeight={TEXT_NOTE_MIN_HEIGHT}
        autoScale
        onResizeStart={setResizeTrue}
        onResize={setResizeTrue}
        onResizeEnd={setResizeFalse}
      />

      <CanvasNodeToolbar
        className="nopan"
        align="center"
        isVisible={exclusivelySelected && !resizing}
        hide={urlEditorOpen}
        offset={18}
      >
        <RichTextToolbar items={TEXT_NODE_TOOLBAR_ITEMS} />
        <Separator />
        <NodeActionsMenu nodeId={id} />
      </CanvasNodeToolbar>

      <Box sx={{ ...styles.outerContainer }}>
        <Box sx={{ width: '100%', height: '100%', cursor: 'text' }}>
          <RichTextEditor
            onChange={handleRichTextChange}
            initialContent={richText}
            placeholder={PLACEHOLDER_TEXT}
            autoFocus
            onBlur={onBlur}
            onHeightChange={setTextEditorHeight}
            editable={editMode}
            hideOutline={!editMode}
          />
          {!editMode && (
            <Box
              className="drag-handle"
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: CSS_Z_INDEX_NODE_HANDLE,
                cursor: 'move',
              }}
              onClick={() => {
                if (exclusivelySelected) {
                  setEditMode(true);
                }
              }}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

const TextNodeWithProviders = (props: NodeProps<RfNodeTextNode>) => {
  return (
    <RichTextEditorProvider>
      <TextNode {...props} />
    </RichTextEditorProvider>
  );
};

export default TextNodeWithProviders;
